import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faUsers } from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';
import { useMeetingsAccess } from '../../utils/meetingsAccess';
import { useMeeting } from '../../context/meetingContextCore';
import { dayText, joinState, rangeText } from './meetingHelpers';
import '../../styles/meetings.css';

/**
 * "Your next meeting" on the dashboard - for the pilot only.
 *
 * Renders nothing at all for anybody the server does not admit, and nothing
 * when there is no meeting coming up, so the dashboard is exactly as it was for
 * everyone else. Looks two weeks ahead and refreshes each minute, so the Join
 * button appears by itself when the room opens.
 */

const AHEAD_DAYS = 14;
const TICK_MS = 60000;

const whenWords = (meeting, now) => {
    const start = new Date(meeting.startsAt);
    const minutes = Math.round((start - now) / 60000);
    if (minutes <= 0) return 'Happening now';
    if (minutes < 60) return `Starts in ${minutes} min`;
    const sameDay = start.toDateString() === now.toDateString();
    const tomorrow = new Date(now.getTime() + 86400000).toDateString() === start.toDateString();
    const day = sameDay ? 'Today' : tomorrow ? 'Tomorrow' : dayText(start, { weekday: 'long', day: 'numeric', month: 'short' });
    return `${day}, ${rangeText(meeting)}`;
};

const NextMeetingCard = () => {
    const { enabled } = useMeetingsAccess();
    const navigate = useNavigate();
    const live = useMeeting();
    const [meetings, setMeetings] = useState([]);
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;
        const load = () => {
            const from = new Date();
            const to = new Date(from.getTime() + AHEAD_DAYS * 86400000);
            api.get('/meetings', { params: { from: from.toISOString(), to: to.toISOString() } })
                .then((res) => { if (alive) setMeetings(res.data || []); })
                .catch(() => { /* the card is a convenience; the dashboard is fine without it */ });
        };
        load();
        const timer = setInterval(() => { setNow(new Date()); load(); }, TICK_MS);
        return () => { alive = false; clearInterval(timer); };
    }, [enabled]);

    if (!enabled) return null;

    const upcoming = meetings
        .filter((m) => m.kind === 'meeting' && !['cancelled', 'ended'].includes(m.status) && new Date(m.endsAt) > now)
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
    if (upcoming.length === 0) return null;

    const next = upcoming[0];
    const here = Boolean(live?.inMeeting) && live.activeId === String(next._id);
    const canJoin = joinState(next, now).can;
    const waiting = next.myRsvp === 'pending' && next.myRole !== 'organizer';

    return (
        <section className="mt-next-card" aria-label="Your next meeting">
            <span className="mt-next-icon"><FontAwesomeIcon icon={faVideo} /></span>
            <div className="mt-next-main">
                <small>Your next meeting{upcoming.length > 1 ? ` · ${upcoming.length - 1} more coming up` : ''}</small>
                <strong>{next.title}</strong>
                <span className="mt-subtle">
                    {whenWords(next, now)}
                    {' · '}<FontAwesomeIcon icon={faUsers} /> {next.participants.length}
                    {waiting ? ' · awaiting your reply' : ''}
                </span>
            </div>
            <div className="mt-next-actions">
                {here ? (
                    <button type="button" className="mt-btn primary" onClick={() => live.expand()}>Return to meeting</button>
                ) : canJoin ? (
                    <button type="button" className="mt-btn primary" onClick={() => navigate(`/meetings/${next._id}/room`)}>Join</button>
                ) : null}
                <button type="button" className="mt-btn ghost" onClick={() => navigate('/meetings')}>All meetings</button>
            </div>
        </section>
    );
};

export default NextMeetingCard;
